import { AlertTriangle, AlertCircle, Info, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export interface AttentionItem {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  message: string;
  action: { label: string; href: string };
}

const severityConfig = {
  critical: {
    border: 'border-l-red-500',
    bg: 'bg-red-50 dark:bg-red-950/20',
    icon: AlertTriangle,
    iconColor: 'text-red-600 dark:text-red-400',
    actionColor:
      'text-red-700 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300',
  },
  warning: {
    border: 'border-l-amber-500',
    bg: 'bg-amber-50 dark:bg-amber-950/20',
    icon: AlertCircle,
    iconColor: 'text-amber-600 dark:text-amber-400',
    actionColor:
      'text-amber-700 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-300',
  },
  info: {
    border: 'border-l-blue-500',
    bg: 'bg-blue-50 dark:bg-blue-950/20',
    icon: Info,
    iconColor: 'text-blue-600 dark:text-blue-400',
    actionColor:
      'text-blue-700 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300',
  },
} as const;

interface AttentionSectionProps {
  items: AttentionItem[];
}

export function AttentionSection({ items }: AttentionSectionProps) {
  return (
    <div className="space-y-2">
      <h2 className="text-sm font-medium text-muted-foreground">
        O que precisa de atenção
      </h2>

      <AnimatePresence mode="sync">
        {items.length === 0 ? (
          <motion.div
            key="all-clear"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950/20"
          >
            <CheckCircle2
              size={18}
              className="shrink-0 text-green-600 dark:text-green-400"
            />
            <span className="text-sm font-medium text-green-700 dark:text-green-400">
              Tudo em dia ✓
            </span>
          </motion.div>
        ) : (
          items.map((item, index) => {
            const config = severityConfig[item.severity];
            const Icon = config.icon;

            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -16, height: 0, marginBottom: 0 }}
                transition={{ duration: 0.25, delay: index * 0.05 }}
                className={`flex items-center justify-between gap-4 rounded-lg border border-border border-l-4 ${config.border} ${config.bg} p-4`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Icon size={18} className={`shrink-0 ${config.iconColor}`} />
                  <span className="text-sm font-medium text-foreground">
                    {item.message}
                  </span>
                </div>

                <a
                  href={item.action.href}
                  className={`shrink-0 text-sm font-semibold underline-offset-2 hover:underline ${config.actionColor}`}
                >
                  {item.action.label}
                </a>
              </motion.div>
            );
          })
        )}
      </AnimatePresence>
    </div>
  );
}
